import { Box, Divider, Stack } from '@mui/material';
import { Currency } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PoolTypography } from '@synergy/components-next';
import { ValueOrNDash } from '@synergy/components';

import { BonusSettlementCustomerDetailsSidebar } from '../models/bonus-settlement-customer-details-sidebar';

interface BonusSettlementDetailSidebarProps {
    bonusSettlement?: BonusSettlementCustomerDetailsSidebar;
    currency: Currency;
}

export const BonusSettlementDetailSidebar = (props: BonusSettlementDetailSidebarProps) => {
    const { currency, bonusSettlement } = props;

    const totalRevenue = (bonusSettlement?.totalByMode.percentage ?? 0) + (bonusSettlement?.totalRevenue ?? 0);

    return (
        <Stack sx={{ p: 4 }} typography="body2" gap={3}>
            <Box>
                <PoolTypography typography="subtitle" translationId="app.common.overallOverview" />
            </Box>
            <Stack gap={1}>
                <Stack direction="row" justifyContent="space-between">
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.mode.percentage" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={bonusSettlement?.totalByMode.percentage}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.mode.revenueAmount" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={bonusSettlement?.totalRevenue}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.mode.totalAmount" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={totalRevenue}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
                <Divider sx={{ borderColor: theme => theme.palette.common.black }} />
                <Stack direction="row" justifyContent="space-between">
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.calculatedBonus" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={bonusSettlement?.totalCalculatedBonus}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.actualBonus" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={bonusSettlement?.totalActualBonus}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
                <Divider sx={{ borderColor: theme => theme.palette.common.black }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 'bold' }}>
                    <Box>
                        <FormattedMessage id="app.bonusSettlements.customerDetail.table.memberBonusPayout" />
                    </Box>
                    <Box>
                        <ValueOrNDash
                            value={bonusSettlement?.totalMemberBonus}
                            valueRender={value => (
                                <FormattedNumber
                                    maximumFractionDigits={2}
                                    style="currency"
                                    currency={currency}
                                    value={value}
                                />
                            )}
                            strictEqual={true}
                        />
                    </Box>
                </Stack>
            </Stack>
        </Stack>
    );
};
